import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type BlogBlock,
  type BlogImages,
  type BlogPostStatus,
  LOCALES,
  type Locale,
  type UpdateBlogPostInput,
} from '@bailafm/shared';
import { ArrowLeft, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BlogBlocksEditor } from '@/components/blog/BlogBlocksEditor';
import { BlogImageField } from '@/components/blog/BlogImageField';
import { ApiClientError, blogApi, type BlogImageTarget } from '@/lib/api';
import type { PixelCrop } from '@/lib/crop-image';
import { cn } from '@/lib/utils';

const WEB_URL = import.meta.env.VITE_WEB_URL ?? 'http://localhost:3000';

const LOCALE_LABELS: Record<Locale, string> = {
  es: 'Español',
  ca: 'Català',
  en: 'English',
};

type TextByLocale = Record<Locale, string>;

const emptyByLocale: TextByLocale = { es: '', ca: '', en: '' };

export default function BlogEditorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<BlogPostStatus>('draft');
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [activeLocale, setActiveLocale] = useState<Locale>('es');
  const [titles, setTitles] = useState<TextByLocale>(emptyByLocale);
  const [subtitles, setSubtitles] = useState<TextByLocale>(emptyByLocale);
  const [excerpts, setExcerpts] = useState<TextByLocale>(emptyByLocale);
  const [seoTitles, setSeoTitles] = useState<TextByLocale>(emptyByLocale);
  const [blocks, setBlocks] = useState<BlogBlock[]>([]);
  const [images, setImages] = useState<BlogImages>({});

  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const hydrate = useCallback(
    (post: Awaited<ReturnType<typeof blogApi.get>>) => {
      setSlug(post.slug);
      setStatus(post.status);
      setPublishedAt(post.publishedAt);
      setTitles({ es: post.title.es ?? '', ca: post.title.ca ?? '', en: post.title.en ?? '' });
      setSubtitles({
        es: post.subtitle.es ?? '',
        ca: post.subtitle.ca ?? '',
        en: post.subtitle.en ?? '',
      });
      setExcerpts({
        es: post.excerpt.es ?? '',
        ca: post.excerpt.ca ?? '',
        en: post.excerpt.en ?? '',
      });
      setSeoTitles({
        es: post.seoTitle.es ?? '',
        ca: post.seoTitle.ca ?? '',
        en: post.seoTitle.en ?? '',
      });
      setBlocks(post.blocks);
      setImages(post.images);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    blogApi
      .get(id)
      .then((post) => {
        if (!cancelled) hydrate(post);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiClientError ? err.message : 'No se pudo cargar la entrada.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, hydrate]);

  const buildTrilingual = (src: TextByLocale) => {
    const out: { es?: string; ca?: string; en?: string } = {};
    for (const l of LOCALES) {
      const v = src[l].trim();
      if (v) out[l] = v;
    }
    return out;
  };

  // Guarda metadata + bloques + estado (el selector del sidebar). Devuelve
  // true si guardó. Las imágenes destacada/thumbnail ya se subieron por su
  // endpoint; aquí solo persistimos el resto.
  const save = useCallback(async (): Promise<boolean> => {
    const title = buildTrilingual(titles);
    if (!title.es && !title.ca && !title.en) {
      toast.error('El título es obligatorio en al menos un idioma.');
      return false;
    }
    const input: UpdateBlogPostInput = {
      title,
      subtitle: buildTrilingual(subtitles),
      excerpt: buildTrilingual(excerpts),
      seoTitle: buildTrilingual(seoTitles),
      blocks,
      status,
      ...(publishedAt !== null ? { publishedAt } : {}),
    };
    setSaving(true);
    try {
      const updated = await blogApi.update(id, input);
      hydrate(updated);
      return true;
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : 'No se pudo guardar la entrada.';
      toast.error(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [titles, subtitles, excerpts, seoTitles, blocks, status, publishedAt, id, hydrate]);

  const handleSave = async () => {
    if (await save()) toast.success('Cambios guardados');
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      // Guardamos primero para que la preview refleje lo que se ve.
      if (!(await save())) return;
      const token = await blogApi.previewToken(id);
      const url = `${WEB_URL}/${activeLocale}/blog/preview/${id}?token=${encodeURIComponent(token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(
        err instanceof ApiClientError ? err.message : 'No se pudo abrir la previsualización.',
      );
    } finally {
      setPreviewing(false);
    }
  };

  // Sube destacada/thumbnail al confirmar el recorte. La subida reemplaza
  // el doc en el servidor (last-write-wins); el guardado no usa ETag, así
  // que no hace falta refrescar nada más que la imagen mostrada.
  const uploadDocImage = useCallback(
    (target: Exclude<BlogImageTarget, 'content'>) =>
      async (file: File, crop: PixelCrop): Promise<string> => {
        const ref = await blogApi.uploadImage(id, target, file, crop);
        setImages((prev) => ({ ...prev, [target]: ref }));
        return ref.url;
      },
    [id],
  );

  if (loading) {
    return <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">Cargando…</p>;
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin/blog')}>
          <ArrowLeft className="size-4" aria-hidden /> Volver
        </Button>
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loadError}
        </p>
      </div>
    );
  }

  const titlePreview = titles.es || titles.ca || titles.en || '(sin título)';

  return (
    <div className="space-y-6 pb-12">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => navigate('/admin/blog')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" aria-hidden /> Blog
          </button>
          <h1 className="text-2xl font-semibold tracking-tight">{titlePreview}</h1>
          <p className="text-xs text-muted-foreground">
            /{slug} ·{' '}
            <span className={status === 'published' ? 'text-emerald-600' : 'text-amber-600'}>
              {status === 'published' ? 'Publicada' : 'Borrador'}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={handlePreview} disabled={previewing}>
            {previewing ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Eye className="size-4" aria-hidden />}
            Previsualizar
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </header>

      {/* Contenido principal + sidebar */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Columna principal */}
        <div className="min-w-0 space-y-6 lg:order-1">
          {/* Selector de idioma (solo afecta a textos/bloques, no a las
              imágenes destacada/thumbnail, que son únicas) */}
          <div className="flex gap-1 rounded-md bg-muted p-1">
            {LOCALES.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setActiveLocale(l)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors',
                  activeLocale === l ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {LOCALE_LABELS[l]}
              </button>
            ))}
          </div>

          {/* Título / subtítulo del idioma activo */}
          <section className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="post-title">Título</Label>
              <Input
                id="post-title"
                value={titles[activeLocale]}
                onChange={(e) => setTitles((prev) => ({ ...prev, [activeLocale]: e.target.value }))}
                placeholder="Título de la noticia…"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="post-subtitle">Subtítulo</Label>
              <Textarea
                id="post-subtitle"
                value={subtitles[activeLocale]}
                onChange={(e) => setSubtitles((prev) => ({ ...prev, [activeLocale]: e.target.value }))}
                placeholder="Subtítulo / entradilla…"
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="post-excerpt">Extracto</Label>
                <span
                  className={cn(
                    'text-xs tabular-nums',
                    excerpts[activeLocale].length > 160
                      ? 'text-destructive'
                      : 'text-muted-foreground',
                  )}
                >
                  {excerpts[activeLocale].length}/160
                </span>
              </div>
              <Textarea
                id="post-excerpt"
                value={excerpts[activeLocale]}
                onChange={(e) => setExcerpts((prev) => ({ ...prev, [activeLocale]: e.target.value }))}
                placeholder="Resumen breve para listados y buscadores…"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Se muestra en las tarjetas del listado/home y se usa como meta
                description si está. ~150-160 caracteres.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="post-seo-title">Título SEO</Label>
              <Input
                id="post-seo-title"
                value={seoTitles[activeLocale]}
                onChange={(e) => setSeoTitles((prev) => ({ ...prev, [activeLocale]: e.target.value }))}
                placeholder="Título para buscadores y redes…"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Sobrescribe el título en buscadores/redes. Si se deja vacío se
                usa el título de la entrada.
              </p>
            </div>
          </section>

          {/* Cuerpo por bloques */}
          <BlogBlocksEditor
            postId={id}
            blocks={blocks}
            activeLocale={activeLocale}
            onChange={setBlocks}
          />
        </div>

        {/* Sidebar: estado + imágenes + fecha */}
        <aside className="space-y-6 lg:order-2 lg:sticky lg:top-6 lg:h-fit">
          {/* Estado + fecha (se guardan al pulsar "Guardar") */}
          <section className="space-y-4 rounded-lg border bg-card p-4">
            <div className="space-y-1.5">
              <Label htmlFor="post-status">Estado</Label>
              <select
                id="post-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as BlogPostStatus)}
                className={cn(
                  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
              </select>
            </div>

            {(status === 'published' || publishedAt) && (
              <div className="space-y-1.5">
                <Label htmlFor="post-published-at">Fecha de publicación</Label>
                <Input
                  id="post-published-at"
                  type="datetime-local"
                  value={toLocalInput(publishedAt)}
                  onChange={(e) => setPublishedAt(fromLocalInput(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Este campo no programa la publicación: el post queda
                  publicado en cuanto guardas. La fecha solo se usa para
                  mostrar y para ordenar el listado.
                </p>
              </div>
            )}
          </section>

          <section className="space-y-4 rounded-lg border bg-card p-4">
            <BlogImageField
              title="Imagen destacada (2.4:1)"
              hint="Hero del detalle · se deriva la imagen para redes (OG)"
              aspect={2.4}
              existingUrl={images.featured?.url}
              onUpload={uploadDocImage('featured')}
            />
            <BlogImageField
              title="Thumbnail (16:9)"
              hint="Listados y home"
              aspect={16 / 9}
              existingUrl={images.thumbnail?.url}
              onUpload={uploadDocImage('thumbnail')}
            />
          </section>
        </aside>
      </div>
    </div>
  );
}

// ISO ↔ valor del input datetime-local (en hora local del navegador).
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const off = date.getTimezoneOffset();
  const local = new Date(date.getTime() - off * 60_000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}
